import { ServiceException } from '@smithy/smithy-client'

import type { StageGroup, TimelineEvent } from '@amzn/aa-workbench-notification-service-client'
import { ChipBackgroundColor } from '@amzn/stencil-react-components/chip'
import { Status } from '@amzn/stencil-react-components/status-indicator'

const HTTP_FORBIDDEN = 403
const HTTP_NOT_FOUND = 404

// Friendly labels over the raw MODULE_* event types and lifecycle statuses. The backend emits raw
// status strings (e.g. REVIEW_MAJOR) as stage names; the prettification lives here on the frontend.
const EVENT_TYPE_LABELS: Record<string, string> = {
    MODULE_DRAFT_SAVED: 'Draft saved',
    MODULE_STATUS_CHANGED: 'Status changed',
    MODULE_NEW_VERSION_STARTED: 'New version started',
    MODULE_APPROVAL_PROCESS_STARTED: 'Review requested',
    MODULE_REVIEW_APPROVED: 'Review approved',
    MODULE_REVIEW_APPROVED_ASYNC: 'Review approved',
    MODULE_REVIEW_REVOKED: 'Review revoked',
    MODULE_REVIEW_REVOKED_ASYNC: 'Review revoked',
    MODULE_UAT_REVIEW_REQUESTED: 'UAT review requested',
    MODULE_TRANSLATION_SUBMITTED: 'Translation submitted',
    MODULE_TRANSLATION_COMPLETED: 'Translation completed',
    MODULE_VERSION_WENT_LIVE: 'Version went live',
    MODULE_ARCHIVED: 'Module archived',
}

// Friendly labels covering BOTH the phase names the backend emits as stageName
// (Draft/Review/UAT/Translation/Published/Live/Archived) and the raw ModuleLifecycleStatus values
// that appear in the "Status changed: X -> Y" transition text on event rows.
const STAGE_NAME_LABELS: Record<string, string> = {
    // Phase names (stage names)
    Draft: 'Draft',
    Review: 'Review',
    UAT: 'UAT',
    Translation: 'Translation',
    Published: 'Published',
    Live: 'Live',
    Archived: 'Archived',
    // Raw lifecycle statuses (used in transition text)
    DRAFT_UNVALIDATED: 'Draft (unvalidated)',
    DRAFT_VALIDATED: 'Draft (validated)',
    REVIEW: 'Review',
    REVIEW_MAJOR: 'Review (major)',
    REVIEW_MINOR: 'Review (minor)',
    REVIEW_PATCH: 'Review (patch)',
    REVIEW_APPROVED: 'Review approved',
    SYNC_IN_PROGRESS: 'Sync in progress',
    TRANSLATION_REQUESTED: 'Translation requested',
    MEDIA_TRANSLATION_REQUIRED: 'Media translation required',
    TRANSLATION_RECEIVED: 'Translation received',
    PUBLISHED: 'Published',
    LIVE: 'Live',
    ARCHIVED: 'Archived',
}

export function getEventLabel(type: string): string {
    return EVENT_TYPE_LABELS[type] || type.toLowerCase().replace(/_/g, ' ')
}

export function getStageLabel(stageName: string | undefined): string {
    if (!stageName) return ''
    return STAGE_NAME_LABELS[stageName] || stageName
}

export function getStageStatus(isDone: boolean, isActive: boolean, wasReverted = false): Status {
    if (isActive) return Status.Waiting
    if (wasReverted) return Status.Declined
    if (isDone) return Status.Positive
    return Status.Neutral
}

// The coarse ModuleVersionStatus rollup -> chip label + colour.
export function getVersionChip(status: string | undefined): {
    label: string
    color: ChipBackgroundColor
} {
    if (status === 'published') return { label: 'Published', color: ChipBackgroundColor.Green }
    if (status === 'archived') return { label: 'Archived', color: ChipBackgroundColor.Red }
    return { label: 'In progress', color: ChipBackgroundColor.Blue }
}

const MS_PER_MINUTE = 60000
const MINUTES_PER_HOUR = 60
const HOURS_PER_DAY = 24

export function formatDurationMs(ms: number | null | undefined): string {
    if (ms == null) return 'ongoing'
    const totalMin = Math.floor(ms / MS_PER_MINUTE)
    if (totalMin < MINUTES_PER_HOUR) return totalMin < 1 ? '<1min' : `${totalMin}min`
    const totalHrs = Math.floor(totalMin / MINUTES_PER_HOUR)
    if (totalHrs < HOURS_PER_DAY) {
        const m = totalMin % MINUTES_PER_HOUR
        return m > 0 ? `${totalHrs}h ${m}min` : `${totalHrs}h`
    }
    const days = Math.floor(totalHrs / HOURS_PER_DAY)
    const h = totalHrs % HOURS_PER_DAY
    return h > 0 ? `${days}d ${h}h` : `${days}d`
}

const MODULE_DRAFT_SAVED = 'MODULE_DRAFT_SAVED'

// Collapses consecutive MODULE_DRAFT_SAVED events by the same actor into one group, the module
// analog of the workflow trail's WORKFLOW_DRAFT_UPDATED collapsing.
export function groupConsecutiveDraftSaves(events: TimelineEvent[]): TimelineEvent[][] {
    const groups: TimelineEvent[][] = []

    for (const event of events) {
        const lastGroup = groups[groups.length - 1]
        const isDraft = event.eventType === MODULE_DRAFT_SAVED
        const continuesPrevious =
            isDraft &&
            lastGroup?.[0]?.eventType === MODULE_DRAFT_SAVED &&
            lastGroup[0].actorUserId === event.actorUserId

        if (continuesPrevious) {
            lastGroup.push(event)
        } else {
            groups.push([event])
        }
    }

    return groups
}

export interface ModuleEventPayload {
    comment: string | undefined
    reviewId: string | undefined
    revisionNumber: string | undefined
    previousStatus: string | undefined
    newStatus: string | undefined
}

const REVISION_DEEP_LINK_PATTERN = /\/revisions\/([^/?]+)/

export function parseEventPayload(event: TimelineEvent): ModuleEventPayload {
    const payload = (event.payload ?? {}) as Record<string, unknown>
    const rawRevision = payload.revisionNumber
    const payloadRevision =
        typeof rawRevision === 'string' || typeof rawRevision === 'number'
            ? String(rawRevision)
            : undefined
    const revisionNumber =
        payloadRevision && payloadRevision.length > 0
            ? payloadRevision
            : REVISION_DEEP_LINK_PATTERN.exec(event.deepLink ?? '')?.[1] ?? undefined
    return {
        comment: (payload.comment || payload.message) as string | undefined,
        reviewId: payload.reviewId as string | undefined,
        revisionNumber,
        previousStatus: payload.previousStatus as string | undefined,
        newStatus: payload.newStatus as string | undefined,
    }
}

export function filterStageEvents(
    stages: StageGroup[],
    filterUsers: string[],
    filterEventTypes: string[]
): StageGroup[] {
    const userSet = new Set(filterUsers.map((u) => u.toLowerCase()))
    const typeSet = new Set(filterEventTypes)
    return stages
        .map((stage) => {
            let filtered: TimelineEvent[] = stage.events ?? []
            if (userSet.size > 0) {
                filtered = filtered.filter((e) => userSet.has((e.actorUserId ?? '').toLowerCase()))
            }
            if (typeSet.size > 0) {
                filtered = filtered.filter((e) => typeSet.has(e.eventType ?? ''))
            }
            return { ...stage, events: filtered }
        })
        .filter((stage) => (stage.events ?? []).length > 0)
}

export function getTimelineErrorMessage(err: unknown): string {
    const statusCode = ServiceException.isInstance(err) ? err.$metadata.httpStatusCode : undefined

    switch (statusCode) {
        case HTTP_FORBIDDEN:
            return 'You don’t have permission to view this audit trail.'
        case HTTP_NOT_FOUND:
            return 'Audit trail is not available for this module.'
        default:
            return 'Failed to load audit trail. Please try refreshing.'
    }
}
